const TOKEN_KEY = "gestionalo.portal.token";
const GUEST_INTAKE_TOKEN_KEY = "gestionalo.guestIntakeToken";
const USER_KEY = "gestionalo.portal.user";
const REFRESH_TOKEN_KEY = "gestionalo.portal.refreshToken";
const ACCESS_EXPIRES_KEY = "gestionalo.portal.accessExpiresAt";

export interface StoredUser {
  id: string;
  email: string;
  displayName: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profileImage?: string | null;
}

/**
 * The intake-scoped token an anonymous visitor holds while the RFQ chat runs
 * (spec b5b59843).
 *
 * A SLOT OF ITS OWN, and that separation is the design rather than tidiness.
 * Put in the ordinary token slot it would do two wrong things: AuthContext
 * would read it and believe the visitor is signed in, so the account gate at
 * the draft-review step would never fire; and the response interceptor's
 * clearAuth on any 401 would wipe it the first time a non-intake call failed —
 * which it would, because the server refuses this token everywhere except the
 * intake routes.
 *
 * Kept in localStorage rather than memory so closing the tab does not strand
 * the conversation: the id rides in the URL, and without the credential a
 * returning visitor would hold a handle to something they can no longer open.
 */
export function getGuestIntakeToken(): string | null {
  return localStorage.getItem(GUEST_INTAKE_TOKEN_KEY);
}

export function setGuestIntakeToken(token: string): void {
  localStorage.setItem(GUEST_INTAKE_TOKEN_KEY, token);
}

/** Called once the guest has become a real account, or its work was claimed. */
export function clearGuestIntakeToken(): void {
  localStorage.removeItem(GUEST_INTAKE_TOKEN_KEY);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function clearRefreshToken(): void {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function getAccessExpiresAt(): Date | null {
  const raw = localStorage.getItem(ACCESS_EXPIRES_KEY);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) return null;
  return new Date(parsed);
}

export function setAccessExpiresAt(iso: string): void {
  localStorage.setItem(ACCESS_EXPIRES_KEY, iso);
}

export function clearAccessExpiresAt(): void {
  localStorage.removeItem(ACCESS_EXPIRES_KEY);
}

export function getStoredUser(): StoredUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredUser;
    if (
      typeof parsed?.id === "string" &&
      typeof parsed?.email === "string"
    ) {
      return {
        id: parsed.id,
        email: parsed.email,
        displayName:
          parsed.displayName != null ? String(parsed.displayName) : null,
        firstName:
          parsed.firstName != null ? String(parsed.firstName) : null,
        lastName:
          parsed.lastName != null ? String(parsed.lastName) : null,
        profileImage:
          parsed.profileImage != null ? String(parsed.profileImage) : null,
      };
    }
  } catch {
    // ignore
  }
  return null;
}

export function setStoredUser(user: StoredUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearStoredUser(): void {
  localStorage.removeItem(USER_KEY);
}

export function clearAuth(): void {
  clearToken();
  clearStoredUser();
  clearRefreshToken();
  clearAccessExpiresAt();
}
